import { getSessionIdleMinutes } from '../config/runtimeConfig';

/**
 * One place for the browser side of a session: the keys it lives under, and the inactivity clock
 * that decides when it is over. Every sign-out path has to clear exactly the same keys, and every
 * guard has to ask the same question about idleness, or a session ends in one tab and survives in
 * another.
 */
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';

// The session itself lives in sessionStorage only: it survives a reload but ends with the tab, so once every
// HRMS tab is closed, opening the app again asks for credentials. A tab opened while another one is still
// open is handed the session over SESSION_CHANNEL (see adoptSessionFromOpenTab).
const SESSION_CHANNEL = 'evita-hrms-session';

/** Written to localStorage when a session is signed out, so every other tab notices through a storage event. */
export const SESSION_ENDED_KEY = 'sessionEndedAt';

// Deliberately in localStorage rather than sessionStorage: it is shared by every tab of the same
// browser, so working in one tab keeps the others alive and timing out ends all of them together.
const LAST_ACTIVITY_KEY = 'lastActivityAt';

// Organization picked in the header switcher, remembered per tenant so it never leaks into another tenant's session.
const SELECTED_ORGANIZATION_KEY = 'selectedOrganization';

// Branch picked in the header branch selector, remembered per tenant and user (each user has their own branch list).
const SELECTED_BRANCH_KEY = 'selectedBranch';

const DEFAULT_IDLE_TIMEOUT_MINUTES = 15;

export const getIdleTimeoutMs = () => {
  const configured = Number(getSessionIdleMinutes());
  const minutes = Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_IDLE_TIMEOUT_MINUTES;
  return minutes * 60 * 1000;
};

export const getStoredToken = () => sessionStorage.getItem(TOKEN_KEY);

export const getStoredRefreshToken = () => sessionStorage.getItem(REFRESH_TOKEN_KEY);

export const getStoredUser = () => sessionStorage.getItem(USER_KEY);

type SessionMessage =
  | { type: 'request' }
  | { type: 'offer' | 'update'; token: string; refreshToken: string; user: string | null }
  | { type: 'ended' };

const openChannel = (): BroadcastChannel | null => {
  try {
    return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SESSION_CHANNEL);
  } catch {
    return null;
  }
};

const postToOtherTabs = (message: SessionMessage) => {
  const channel = openChannel();
  if (!channel) return;
  channel.postMessage(message);
  channel.close();
};

const writeSession = (token: string, refreshToken: string, user: string | null) => {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  if (user) sessionStorage.setItem(USER_KEY, user);
};

// Other open tabs get rotated tokens too, so a refresh in one tab never leaves another holding a spent refresh token.
const shareSessionWithOtherTabs = () => {
  const token = getStoredToken();
  const refreshToken = getStoredRefreshToken();
  if (token && refreshToken) postToOtherTabs({ type: 'update', token, refreshToken, user: getStoredUser() });
};

export const saveStoredTokens = (token: string, refreshToken: string) => {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  shareSessionWithOtherTabs();
};

export const saveStoredUser = (user: unknown) => {
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
  shareSessionWithOtherTabs();
};

// Sessions saved by earlier versions also sat in localStorage, where they outlived every tab.
const LEGACY_SHARED_KEYS = [TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY];
const removeLegacySharedSession = () => LEGACY_SHARED_KEYS.forEach((key) => localStorage.removeItem(key));

/**
 * Called once at startup, before the store reads the session. A tab with no session of its own (a new tab,
 * not a reload) asks the other open HRMS tabs for theirs and waits briefly for an answer. With no other tab
 * open there is no answer, and the tab starts signed out.
 */
export const adoptSessionFromOpenTab = (waitMs = 300): Promise<void> => {
  removeLegacySharedSession();
  if (getStoredToken()) return Promise.resolve();

  const channel = openChannel();
  if (!channel) return Promise.resolve();

  return new Promise((resolve) => {
    const finish = () => {
      window.clearTimeout(timer);
      channel.close();
      resolve();
    };
    const timer = window.setTimeout(finish, waitMs);
    channel.onmessage = (event: MessageEvent<SessionMessage>) => {
      if (event.data?.type !== 'offer') return;
      writeSession(event.data.token, event.data.refreshToken, event.data.user);
      finish();
    };
    const request: SessionMessage = { type: 'request' };
    channel.postMessage(request);
  });
};

/** Keeps this tab answering session requests from new tabs and following other tabs' refreshes and sign-outs. */
export const listenForOtherTabs = () => {
  const channel = openChannel();
  if (!channel) return;
  channel.onmessage = (event: MessageEvent<SessionMessage>) => {
    const message = event.data;
    if (message?.type === 'request') {
      const token = getStoredToken();
      const refreshToken = getStoredRefreshToken();
      if (token && refreshToken && !isSessionIdleExpired()) {
        const offer: SessionMessage = { type: 'offer', token, refreshToken, user: getStoredUser() };
        channel.postMessage(offer);
      }
    } else if (message?.type === 'update' && getStoredToken()) {
      writeSession(message.token, message.refreshToken, message.user);
    } else if (message?.type === 'ended') {
      discardLocalSession();
    }
  };
};

export const markSessionActivity = () => {
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
};

/** Forgets this tab's copy of the session without signing anyone else out (e.g. a tab that starts signed out). */
export const discardLocalSession = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  removeLegacySharedSession();
};

/** Signs the session out in this tab and every other open tab. */
export const clearStoredSession = () => {
  // Only the tab that actually held the session tells the others, so a tab reacting to their sign-out stays quiet.
  const hadSession = !!getStoredToken();
  discardLocalSession();
  localStorage.removeItem(LAST_ACTIVITY_KEY);
  localStorage.removeItem(SELECTED_ORGANIZATION_KEY);
  localStorage.removeItem(SELECTED_BRANCH_KEY);
  if (hadSession) {
    localStorage.setItem(SESSION_ENDED_KEY, String(Date.now()));
    postToOtherTabs({ type: 'ended' });
  }
};

/** The organization selected for this tenant, or null for "All Organizations". */
export const getSelectedOrganizationId = (tenantId?: string | null): string | null => {
  if (!tenantId) return null;
  try {
    const stored = JSON.parse(localStorage.getItem(SELECTED_ORGANIZATION_KEY) || 'null');
    return stored?.tenantId === tenantId && stored?.organizationId ? String(stored.organizationId) : null;
  } catch {
    return null;
  }
};

export const setSelectedOrganizationId = (tenantId: string, organizationId: string | null) => {
  try {
    if (organizationId) {
      localStorage.setItem(SELECTED_ORGANIZATION_KEY, JSON.stringify({ tenantId, organizationId }));
    } else {
      localStorage.removeItem(SELECTED_ORGANIZATION_KEY);
    }
  } catch {
    // Storage blocked: the switcher just falls back to All Organizations.
  }
};

/** The branch selected in the header for this tenant and user, or null for "All Branches". */
export const getSelectedBranchId = (tenantId?: string | null, userId?: string | null): string | null => {
  if (!tenantId || !userId) return null;
  try {
    const stored = JSON.parse(localStorage.getItem(SELECTED_BRANCH_KEY) || 'null');
    return stored?.tenantId === tenantId && stored?.userId === userId && stored?.branchId ? String(stored.branchId) : null;
  } catch {
    return null;
  }
};

export const setSelectedBranchId = (tenantId: string, userId: string, branchId: string | null) => {
  try {
    if (branchId) {
      localStorage.setItem(SELECTED_BRANCH_KEY, JSON.stringify({ tenantId, userId, branchId }));
    } else {
      localStorage.removeItem(SELECTED_BRANCH_KEY);
    }
  } catch {
    // Storage blocked: the selector just falls back to All Branches.
  }
};

/**
 * A stored token with no activity stamp is a session that predates this policy, or one whose stamp
 * was cleared behind our back. Neither can be dated, so neither is trusted: the user signs in again.
 */
export const isSessionIdleExpired = () => {
  const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
  if (!Number.isFinite(lastActivity) || lastActivity <= 0) return true;

  return Date.now() - lastActivity > getIdleTimeoutMs();
};

/**
 * A session found already idle-expired when the app boots has its token cleared before anything can
 * reach the network, so the refresh token is parked here for the session watcher to revoke once it
 * mounts. Covers the browser that was closed and reopened a day later.
 */
let staleRefreshToken: string | null = null;

export const rememberStaleRefreshToken = (token: string | null) => {
  staleRefreshToken = token;
};

export const takeStaleRefreshToken = () => {
  const token = staleRefreshToken;
  staleRefreshToken = null;
  return token;
};

export const SESSION_TIMEOUT_REDIRECT = '/login?reason=timeout';
